/*
  Behaviour for the PDP availability and questions blocks.

  Loaded by both snippets, so the customElements guards matter: the script tag
  can legitimately appear twice on one page.
*/

/* --- Availability -------------------------------------------------------- *
   The theme already re-fetches the whole section on every variant change and
   publishes the parsed document on `variantChange`. Rather than recompute the
   stock state in JS (and risk drifting from the Liquid), pull the freshly
   rendered block straight out of that document.
 * ------------------------------------------------------------------------- */
if (!customElements.get('product-availability')) {
  customElements.define(
    'product-availability',
    class ProductAvailability extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.render(event.data.html);
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      render(html) {
        const target = this.querySelector(`#Availability-${this.sectionId}`);
        const source = html?.getElementById(`Availability-${this.sectionId}`);
        if (!target || !source) return;

        // The state lives in the class name, so copy that as well as the text.
        target.className = source.className;
        target.innerHTML = source.innerHTML;
      }
    }
  );
}

/* --- Questions ----------------------------------------------------------- */
if (!customElements.get('product-questions')) {
  customElements.define(
    'product-questions',
    class ProductQuestions extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-pdp-open-chat]');
        this.button?.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        event.preventDefault();
        if (this.openChat()) return;

        const fallback = this.dataset.fallbackUrl;
        if (fallback) window.location.href = fallback;
      }

      openChat() {
        // Re:amaze — what this store actually runs.
        if (typeof window.reamaze?.popup === 'function') {
          window.reamaze.popup();
          return true;
        }

        // Other common widgets, in case the chat app is ever swapped. Presence
        // is checked separately from the call, because every one of these
        // returns undefined on success.
        const vendors = [
          { present: () => typeof window.GorgiasChat?.open === 'function', open: () => window.GorgiasChat.open() },
          { present: () => typeof window.tidioChatApi?.open === 'function', open: () => window.tidioChatApi.open() },
          { present: () => typeof window.Tawk_API?.maximize === 'function', open: () => window.Tawk_API.maximize() },
          { present: () => typeof window.$crisp?.push === 'function', open: () => window.$crisp.push(['do', 'chat:open']) },
          { present: () => typeof window.Intercom === 'function', open: () => window.Intercom('show') },
          { present: () => typeof window.zE === 'function', open: () => window.zE('messenger', 'open') },
        ];

        for (const vendor of vendors) {
          try {
            if (vendor.present()) {
              vendor.open();
              return true;
            }
          } catch (error) {
            /* not ready — try the next one */
          }
        }

        // Last resort: click whatever launcher bubble is on the page.
        const launcher = document.querySelector(
          '#reamaze-widget, .rmz-chat-bubble, #gorgias-chat-container button, #tidio-chat iframe, .crisp-client [data-visible]'
        );
        if (launcher) {
          launcher.click();
          return true;
        }

        return false;
      }
    }
  );
}
